/**
 * Widget Liste - Affiche les offres touristiques sous forme de cards avec filtres
 */

import { html, unsafeCSS, CSSResultGroup } from "lit";
import { customElement, state } from "lit/decorators.js";
import { ref, createRef, Ref } from "lit/directives/ref.js";
import { BaseWidget } from "../components/BaseWidget";
import "../components/FilterPanel";
import "../components/WidgetHeader";
import "../components/WidgetLayout";
import "../components/Icon";
import "../components/ListItemCard";
import { ObjetTouristique } from "../types/apidae.d";
import { getFieldData, getFieldIconName } from "../utils/itemFieldUtils";
import {
  getOpenSystemWidgetFromItem,
  isOpenSystemConfigured,
  getEllohaWidgetFromItem,
  isEllohaConfigured,
} from "../utils/displayedFieldUtils";
import { getPriceFromBatch } from "../services/EllohaService";
import listWidgetStyles from "../styles/list-widget.css?inline";
import {
  formatDate,
  getEllohaCultureFromLanguage,
  getImageUrl,
  getLabel,
  t,
} from "../utils/widgetsUtils";
import { DisplayedFieldId } from "../types/widgets.d";

@customElement("apidae-widget-liste")
export class ListWidget extends BaseWidget {
  static styles: CSSResultGroup = [
    BaseWidget.sharedStyles,
    unsafeCSS(listWidgetStyles),
  ];

  @state()
  private failedImages: Set<number> = new Set();

  @state()
  private loadedImages: Set<number> = new Set();

  /** Cache prix minimum Elloha par idProduct (pour tooltip "À partir de X €") */
  @state()
  private ellohaPrices: Map<string, number> = new Map();

  private sentinelRef: Ref<HTMLDivElement> = createRef();
  private intersectionObserver?: IntersectionObserver;

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  firstUpdated() {
    // Configurer l'IntersectionObserver pour le scroll infini
    this.setupIntersectionObserver();
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);

    // Reconfigurer l'observer si le sentinel change
    if (changedProperties.has("items") || changedProperties.has("hasMore")) {
      requestAnimationFrame(() => {
        this.setupIntersectionObserver();
      });
    }

    if (changedProperties.has("items")) {
      this.loadEllohaPrices();
    }

    if (
      this.changedNotNull(changedProperties, "currentPage") &&
      this.currentPage === 1
    ) {
      this.resetScroll();
    }
  }

  private async loadEllohaPrices() {
    const displayedFields = this.config?.contentCustomization?.displayedFields;
    if (!isEllohaConfigured(displayedFields) || !this.items?.length) {
      this.ellohaPrices = new Map();
      return;
    }
    const field = displayedFields?.find(
      (f) => f.id === DisplayedFieldId.DISPONIBILITES_ELLOHA,
    );
    const data = field?.data as { idBookingEngine?: string } | undefined;
    const idBookingEngine = data?.idBookingEngine;
    if (!idBookingEngine) return;

    const idProducts: string[] = [];
    for (const item of this.items) {
      const elloha = getEllohaWidgetFromItem(item);
      if (elloha?.idProduct) idProducts.push(elloha.idProduct);
    }
    if (idProducts.length === 0) {
      this.ellohaPrices = new Map();
      return;
    }

    const culture = getEllohaCultureFromLanguage(this.language);
    const map = await getPriceFromBatch(idBookingEngine, idProducts, culture);
    this.ellohaPrices = new Map(map);
    this.requestUpdate();
  }

  protected async handleFilterChange(e: CustomEvent) {
    super.handleFilterChange(e);

    // Réinitialiser le scroll en haut pour éviter que l'IntersectionObserver déclenche un chargement immédiat
    this.resetScroll();
  }

  protected async handleClearFilters() {
    super.handleClearFilters();

    // Réinitialiser le scroll en haut pour éviter que l'IntersectionObserver déclenche un chargement immédiat
    this.resetScroll();
  }

  private resetScroll() {
    requestAnimationFrame(() => {
      const scrollContainer = this.shadowRoot?.querySelector(
        ".list-main",
      ) as HTMLElement;
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
      // Réinitialiser l'IntersectionObserver pour qu'il ne déclenche pas immédiatement
      this.setupIntersectionObserver();
    });
  }

  private setupIntersectionObserver() {
    // Détruire l'ancien observer s'il existe
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    // Si pas de sentinel ou pas de données à charger, ne pas créer d'observer
    if (!this.sentinelRef.value || !this.hasMore) {
      return;
    }

    // Trouver le conteneur scrollable
    const scrollContainer = this.shadowRoot?.querySelector(
      ".list-main",
    ) as HTMLElement;
    if (!scrollContainer) {
      return;
    }

    // Créer un nouvel observer avec le conteneur scrollable comme root
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.hasMore && !this.loading) {
            if (this.isChildWidget) {
              this.dispatchEvent(
                new CustomEvent("child-context-change", {
                  detail: {
                    source: "list",
                    loadNextPage: true,
                  },
                  bubbles: true,
                  composed: true,
                }),
              );
            } else {
              this.loadNextPage(false);
            }
          }
        });
      },
      {
        root: scrollContainer, // Utiliser le conteneur scrollable comme root
        rootMargin: "100px", // Charger quand on est à 100px du bas
      },
    );

    this.intersectionObserver.observe(this.sentinelRef.value);
  }

  protected renderWidget() {
    if (this.loading) {
      return html`
        <div class="loading">
          <div class="loading-spinner"></div>
          <p>${t("loading", this.language)}</p>
        </div>
      `;
    }

    if (this.error) {
      return this.renderErrorScreen();
    }

    const hasFilterFields =
      this.config?.contentCustomization?.filterFields &&
      this.config.contentCustomization.filterFields.length > 0;
    const showFiltersConfig =
      hasFilterFields &&
      (this.config?.templateSpecificConfig as any)?.config?.showFilters !==
        false;
    const showFilters = showFiltersConfig && this.filtersVisible;
    const layoutFraction = this.getLayoutFraction();

    const visual = this.config?.visualCustomization ?? null;
    return html`
      <apidae-widget-layout
        .config=${{
          fraction: this.hideHeaderAndFilters ? "full" : layoutFraction,
          showFilters: this.hideHeaderAndFilters ? false : showFilters,
          headerSlot: this.hideHeaderAndFilters ? undefined : "header",
          filtersSlot: this.hideHeaderAndFilters
            ? undefined
            : showFilters
              ? "filters"
              : undefined,
          listSlot: "list",
        }}
        .visualCustomization=${visual}
      >
        ${!this.hideHeaderAndFilters
          ? html`
              <div slot="header">
                <apidae-widget-header
                  .config=${{
                    widgetName: this.config?.name,
                    logo: this.config?.visualCustomization?.logo,
                    logoUrl: this.config?.visualCustomization?.logoUrl,
                    language: this.language,
                    showFiltersButton: showFiltersConfig,
                    showClearButton: showFiltersConfig,
                    hasActiveFilters: this.hasActiveFilters(),
                    showSwitcher: false,
                    filtersVisible: this.filtersVisible,
                    resultsCount: this.getResultsCountForDisplay(),
                    primaryColor:
                      this.config?.visualCustomization?.colors?.primary,
                    secondaryColor:
                      this.config?.visualCustomization?.colors?.secondary,
                  }}
                  .visualCustomization=${visual}
                  @toggle-filters=${this.handleToggleFilters}
                  @clear-filters=${this.handleClearFilters}
                ></apidae-widget-header>
              </div>

              ${showFilters
                ? html`
                    <div slot="filters" style="flex: 1;">
                      <apidae-filter-panel
                        .filterGroups=${this.filterGroups}
                        .language=${this.language}
                        .activeFilters=${this.activeFilters}
                        .dateRange=${this.dateRange}
                        .loadingMoreFacetId=${this.loadingMoreFacetId}
                        .visualCustomization=${visual}
                        @filter-change=${this.handleFilterChange}
                        @load-more-facet=${this.handleLoadMoreFacet}
                      ></apidae-filter-panel>
                    </div>
                  `
                : ""}
            `
          : ""}

        <div slot="list" class="list-main">
          ${this.items.length === 0 && !this.hideEmptyState
            ? html`
                <div class="empty-state">
                  <div class="empty-state-title">
                    ${t("noResultsFound", this.language) ||
                    "Aucun résultat trouvé."}
                  </div>
                  <div class="empty-state-subtitle">
                    ${t("adjustSearchWithFilters", this.language) ||
                    "Ajustez votre recherche grâce aux filtres."}
                  </div>
                </div>
              `
            : html`
                <div class="list-container">
                  <div class="list-grid">
                    ${this.items.map((item: ObjetTouristique) =>
                      this.renderListItem(item),
                    )}
                  </div>

                  ${this.hasMore
                    ? html`<div
                        ${ref(this.sentinelRef)}
                        style="height: 1px;"
                      ></div>`
                    : ""}
                </div>
              `}
        </div>
      </apidae-widget-layout>
    `;
  }

  private renderListItem(item: ObjetTouristique) {
    const imageUrl = getImageUrl(item, "diaporama", this.language);
    const nom = getLabel(item.nom, this.language);
    const displayedFields =
      this.config?.contentCustomization?.displayedFieldsPreview || [];
    const hasOpenSystem =
      isOpenSystemConfigured(
        this.config?.contentCustomization?.displayedFields,
      ) && !!getOpenSystemWidgetFromItem(item)?.ui;
    const ellohaFromItem = getEllohaWidgetFromItem(item);
    const hasElloha =
      isEllohaConfigured(this.config?.contentCustomization?.displayedFields) &&
      !!ellohaFromItem?.idProduct;
    const ellohaPriceFrom =
      hasElloha && ellohaFromItem?.idProduct
        ? this.ellohaPrices.get(ellohaFromItem.idProduct) ?? null
        : null;

    return html`
      <apidae-list-item-card
        data-item-id="${item.id}"
        .item=${item}
        .imageUrl=${imageUrl}
        .nom=${nom}
        .displayedFields=${displayedFields}
        .hasOpenSystem=${hasOpenSystem}
        .hasElloha=${hasElloha}
        .ellohaPriceFrom=${ellohaPriceFrom}
        .typography=${this.config?.visualCustomization?.typography}
        .language=${this.language}
        .apiFilters=${this.apiFilters}
        .selectedItemId=${this.selectedItemId ?? null}
        .failedImages=${this.failedImages}
        .loadedImages=${this.loadedImages}
        .t=${(key: string) => t(key, this.language)}
        .getFieldData=${(
          it: ObjetTouristique,
          fieldId: string,
          _apiFilters: unknown,
          _language: string,
        ) => this.getFieldData(it, fieldId)}
        .getFieldIconName=${getFieldIconName}
        .onItemClick=${(clickedItem: ObjetTouristique) => {
          if (this.disableDetailView) {
            this.dispatchEvent(
              new CustomEvent("item-click", {
                detail: { item: clickedItem },
                bubbles: true,
                composed: true,
              }),
            );
          } else {
            this.handleItemClick(clickedItem);
          }
        }}
        .onImageLoad=${(id: number) => {
          this.loadedImages.add(id);
          this.requestUpdate();
        }}
        .onImageError=${(id: number) => {
          this.failedImages.add(id);
          this.requestUpdate();
        }}
        .visualCustomization=${this.config?.visualCustomization ?? null}
      ></apidae-list-item-card>
    `;
  }

  private getFieldData(
    item: ObjetTouristique,
    fieldId: string,
  ): { value: string; url?: string } | null {
    return getFieldData(item, fieldId, {
      language: this.language,
      getLabel: (libelle) => getLabel(libelle, this.language),
      formatDate: (dateStr, options) =>
        formatDate(dateStr, this.language, options),
      t: (key) => t(key, this.language),
    });
  }

  render() {
    return html`
      <div class="widget-container">
        <div class="widget-content">${this.renderWidget()}</div>
        ${this.renderDetailView()} ${this.renderToast()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "apidae-widget-liste": ListWidget;
  }
}
